import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthSession, Claim, SignUpReponse, UserClaims } from './auth.types';
import { YaloService } from '../yalo/services/yalo.service';
import { RsaEncryptService } from '../utils/rsa-encrypt.service';
import { StorageService } from '../utils/storage.service';
import { stringToBoolean } from '../yalo/yalo.utils';
import { SignUpData } from 'src/app/pages/auth/sign-up/types/sign-up.types';
import { RenewData, RenewResponse } from '@auth/renew/types/renew-types';
import {
  SuscripcionData,
  SuscripcionResponse,
} from '@pages/organizacion/suscripcion/types/suscripcion-types';

@Injectable()
export class AuthService {
  private ACCESS_TOKEN = 'accessToken';
  private REFRESH_TOKEN = 'refreshToken';
  private EXPIRES_IN = 'expiresIn';
  private AUTHENTICATED = 'authenticated';

  constructor(
    private _http: HttpClient,
    private _yalo: YaloService,
    private _rsa: RsaEncryptService,
    private _storage: StorageService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access tokens
   */
  get accessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN) ?? '';
  }

  set accessToken(token) {
    localStorage.setItem(this.ACCESS_TOKEN, token);
  }

  get refreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN) ?? '';
  }

  set refreshToken(token) {
    localStorage.setItem(this.REFRESH_TOKEN, token);
  }
  set expiresIn(expiresIn) {
    localStorage.setItem(this.EXPIRES_IN, expiresIn);
  }

  get expiresIn() {
    return localStorage.getItem(this.EXPIRES_IN) ?? '';
  }

  set authenticated(value: boolean) {
    this._storage.set(this.AUTHENTICATED, value);
  }

  getAuthenticated() {
    return new Promise<boolean>((resolve, reject) => {
      this._storage.get(this.AUTHENTICATED).then((value) => {
        if (value) {
          resolve(true);
        }
        reject(false);
      });
    });
  }

  setClaim({ type, value }: Claim) {
    if (value === 'True' || value === 'False') {
      value = JSON.stringify(stringToBoolean(value));
    }
    localStorage.setItem(type, value);
  }

  // getClaim(type: string) {
  //   return localStorage.getItem(type) ?? '';
  // }
  getClaim(type: string): string {
    const token = localStorage.getItem('accessToken'); // 🔹 Obtiene el token del localStorage
    if (!token) {return '';} // 🔹 Si no hay token, retorna vacío

    try {
      const payloadBase64 = token.split('.')[1]; // 🔹 Extrae la parte del payload
      const payloadDecoded = atob(payloadBase64); // 🔹 Decodifica de Base64 a JSON
      const payload = JSON.parse(payloadDecoded); // 🔹 Convierte a objeto

      return payload[type] ?? ''; // 🔹 Retorna el claim solicitado
    } catch (error) {
      console.error('Error decodificando el token:', error);
      return '';
    }
  }

  signIn(
    credentials: { username: string; password: string; grant_type: string },
    alreadyEncrypted = false
  ) {
    if (!alreadyEncrypted) {
      credentials.password = this._rsa
        .encryptWithPublicKey(credentials.password)
        .replace(/[+]/g, '@');
    }
  
    const body = new URLSearchParams(credentials).toString();
  
    return this._http.post<AuthSession>(`${environment.ROOT_API_URL}/token`, body).pipe(
      concatMap((session) => {
        this.accessToken = session.access_token;
        this.refreshToken = session.refresh_token;
        this.authenticated = true;
        return this._getUserClaims();
      }),
      tap(({ claims, expirate }) => {
        this.expiresIn = expirate.toString();
        claims.forEach((claim) => this.setClaim(claim));
      }),
      concatMap(() => this._yalo.getMultiFactorAuth()),
      tap((mfa) => {
        const requiresMFA = Object.values(mfa).some(Boolean);
        if (requiresMFA) this.authenticated = false;
      })
    );
  }
  

  signUp(data: SignUpData) {
    return this._http
      .post<SignUpReponse>(`${environment.API_URL}/RegistroUsuarios`, data)
      .pipe(
        catchError((error) => throwError(error)),
        concatMap(() =>
          this.signIn(
            {
              username: data.correoUsuario,
              password: data.claveUsuario,
              grant_type: 'password',
            },
            true
          )
        )
      );
  }

  refrescarToken(
    CodOrganizacion: string,
    CodOrganizacionAUTH: string,
    PSK: string,
    Tipo: number
  ): Observable<string> {
    const grantType = 'refresh_token';
    const refreshToken = this.refreshToken;
    // const accessToken = `Bearer ${this.accessToken}`;
    const body: HttpParams = new HttpParams()
      .set(`grant_type`, grantType)
      .set(`refresh_token`, refreshToken);
    let newHeaders;
    if (Tipo === 1) {
      newHeaders = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        CodOrganizacion,
        CodOrganizacionAUTH,
      });
    } else if (Tipo === 2) {
      newHeaders = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        PSK,
      });
    }
    return this._http
      .post<any>(environment.ROOT_API_URL + '/token', body.toString(), {
        headers: newHeaders,
      })
      .pipe(
        tap((res) => this.doLoginUser(res)),
        map((res) => res.access_token as string)
      );
  }

  private doLoginUser(authResult) {
    this.accessToken = authResult.access_token;
    this.refreshToken = authResult.refresh_token;
  }

  signOut() {
    // Stop signalr connection
    // this._signalR.stop();

    // Clear the local storage
    localStorage.clear();
    this._storage.clear();
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }
    return of(true);
  }

  private _getUserClaims() {
    return this._http.get<UserClaims>(`${environment.API_URL}/getUser`);
  }

  renewSubscription(renew: RenewData) {
    return this._http.post<RenewResponse>(
      `${environment.API_URL}/Stripe/Payments/Subs/Renew`,
      renew
    );
  }

  newOrgSubscription(suscripcion: SuscripcionData) {
    return this._http.post<SuscripcionResponse>(
      `${environment.API_URL}/Stripe/Payments/Subs/NewOrg`,
      suscripcion
    );
  }
}
